import { useEffect, useState, useMemo } from 'react';
import { COLORS, ColorOption } from './color-palete-map';

type ColorPickerProps = {
  dropdownRef: React.RefObject<HTMLDivElement>;
  initialColor: string;
  onChange: (color: string) => void;
};

export const useColorPicker = ({
  dropdownRef,
  initialColor,
  onChange,
}: ColorPickerProps) => {
  const [colors, setColors] = useState<ColorOption[]>(COLORS);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<ColorOption | null>(null);
  const [search, setSearch] = useState('');
  const showColorExample = selected?.hex !== 'HEXCOLOR';

  const inputValue = useMemo(() => {
    if (isOpen) {
      return search;
    }
    if (initialColor) {
      return `${initialColor} - ${selected?.label}`;
    }
    if (!selected?.hex) {
      return 'Choose a color';
    }
    return `${selected?.hex} - ${selected?.label}`;
  }, [isOpen, initialColor, selected?.hex, selected?.label, search]);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      toggleDropdown();
    }
  };

  const handleSelect = (option: ColorOption) => {
    setSelected(option);
    setIsOpen(false);
    onChange(option.hex);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setColors(
      COLORS.filter(
        (color) =>
          color.label.toLowerCase().includes(e.target.value.toLowerCase()) || // search by label disregarding case
          color.hex.toLowerCase().includes(e.target.value.toLowerCase()) // search by hex
      )
    );
  };

  // Populates the dropdown with the values from the JSON file
  useEffect(() => {
    if (initialColor) {
      setSelected(COLORS.find((color) => color.hex === initialColor) || null);
    }
  }, [initialColor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch('');
        setColors(COLORS);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  return {
    colors,
    isOpen,
    selected,
    inputValue,
    toggleDropdown,
    handleSelect,
    handleKeyDown,
    handleSearch,
    showColorExample,
  };
};
